var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertToNumber3, DEG2RAD } from "flavr-common";
import { Vector3 } from "flavr-math";
import { navMeshService } from "../navMesh";
import { ConeGeometry, DoubleSide, Group, Mesh, MeshBasicMaterial, Raycaster, TorusGeometry } from "three";
import { mainScene } from "../rendering/mainScene";
import { RayCurveMesh } from "./RayCurve";
import { configurePostprocessingOverlay } from "../rendering/postprocessing/PostprocessingOverlay";
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const UNIT_Y = new Vector3(0, 1, 0);
const MISS_OPACITY = 0.1;
const HIT_OPACITY = 0.6;
const MISS_COLOR = 0xff0000;
const HIT_COLOR = 0x1f385c;
const DRAW_TIME_S = 0.6;
// https://aframe.io/blog/teleport-component/
export class Teleporter {
    constructor() {
        this.rayCurve = new RayCurveMesh(20, 0.025);
        this.hitMarker = new Group();
        this.waitForRaycast = false;
        this.raycaster = new Raycaster();
        this.timeTeleporting = 0;
        this.position = new Vector3();
        this.direction = new Vector3();
        this.speed = 12;
        this.characterScale = 1;
        this.p0 = new Vector3();
        this.v0 = new Vector3();
        this.updatedTime = 0;
        this.hitPoint = new Vector3();
        this.hasHit = false;
        this.viewArrowGroup = new Group();
        this.enabled = false;
        mainScene.add(this.rayCurve);
        this.hitMarker = this.createHitMarker(0.25, 0.6, 0x4d8be1);
        configurePostprocessingOverlay(this.hitMarker);
        this.hitMarker.visible = false;
        mainScene.add(this.hitMarker);
    }
    setHitMarkerVisible(visible) {
        this.hitMarker.visible = visible;
    }
    isEnabled() {
        return this.enabled;
    }
    setEnabled(enabled) {
        if (enabled) {
            this.start();
        }
        else {
            this.stop();
        }
    }
    setViewArrowYaw(yaw) {
        this.viewArrowYaw = yaw;
        this.updateViewArrowRotation();
    }
    getViewArrowYaw() {
        return this.viewArrowYaw;
    }
    getViewArrowWorldYaw() {
        if (!this.viewArrowGroup.visible) {
            return undefined;
        }
        const worldDirection = this.viewArrowGroup.getWorldDirection(new Vector3());
        return Math.atan2(worldDirection.x, worldDirection.z);
    }
    getDesiredWorldYaw() {
        return this.getViewArrowWorldYaw();
    }
    getHitPoint() {
        if (!this.hasHit)
            return;
        return this.hitPoint;
    }
    destroy() {
        mainScene.remove(this.rayCurve);
        mainScene.remove(this.hitMarker);
    }
    start() {
        this.enabled = true;
        this.timeTeleporting = 0;
        this.rayCurve.visible = false;
        this.rayCurve.getMaterial().opacity = MISS_OPACITY;
        this.rayCurve.getMaterial().color.set(MISS_COLOR);
        this.rayCurve.getMaterial().needsUpdate = true;
        this.hitMarker.visible = false;
        this.viewArrowGroup.visible = false;
    }
    stop() {
        this.enabled = false;
        this.rayCurve.visible = false;
        this.hitMarker.visible = false;
        this.viewArrowGroup.visible = false;
    }
    intersectParabolicCurveWithNavMesh(dt) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.enabled || this.waitForRaycast) {
                this.hitMarker.visible = false;
                return false;
            }
            this.timeTeleporting += dt;
            this.rayCurve.setDirection(this.direction);
            this.updateViewArrowRotation();
            this.v0.copy(this.direction).multiplyScalar(this.speed * Math.sqrt(this.characterScale));
            this.p0.copy(this.position);
            let collidedIndex = this.rayCurve.numPoints - 1;
            this.hasHit = false;
            this.waitForRaycast = true;
            try {
                const result = yield this.checkParabolicCurveIntersection(this.p0, this.v0, this.hitPoint);
                if (result >= 0) {
                    collidedIndex = result;
                    this.hasHit = true;
                }
            }
            catch (_a) { }
            this.waitForRaycast = false;
            // The teleporter may have been disabled while the async raycast was in flight.
            // In that case we must not make the visualization visible again afterward.
            if (!this.enabled) {
                this.rayCurve.visible = false;
                this.hitMarker.visible = false;
                this.viewArrowGroup.visible = false;
                return false;
            }
            const percentToDraw = this.timeTeleporting > DRAW_TIME_S ? 1 : this.timeTeleporting / DRAW_TIME_S;
            const percentRaycasted = collidedIndex / (this.rayCurve.numPoints - 1);
            const segmentT = (percentToDraw * percentRaycasted) / (this.rayCurve.numPoints - 1);
            for (let i = 0; i < this.rayCurve.numPoints; i++) {
                const t = i * segmentT;
                this.parabolicCurve(this.p0, this.v0, t, tmpVector31);
                this.rayCurve.setPoint(tmpVector31, i);
            }
            const color = this.hasHit ? HIT_COLOR : MISS_COLOR;
            const opacity = this.hasHit && this.timeTeleporting >= DRAW_TIME_S ? HIT_OPACITY : MISS_OPACITY;
            this.rayCurve.getMaterial().color.set(color);
            this.rayCurve.getMaterial().opacity = opacity;
            this.rayCurve.visible = true;
            if (this.hasHit) {
                this.hitMarker.position.copy(this.hitPoint);
            }
            return this.hasHit;
        });
    }
    createHitMarker(hitCylinderRadius, outerRadius, hitCylinderColor) {
        const hitMarker = new Group();
        this.torus = new Mesh(new TorusGeometry(hitCylinderRadius, 0.01, 16, 18, 360 * DEG2RAD), new MeshBasicMaterial({
            color: hitCylinderColor,
            side: DoubleSide,
            transparent: true
        }));
        this.torus.rotation.x = 90 * DEG2RAD;
        hitMarker.add(this.torus);
        this.outerTorus = new Mesh(new TorusGeometry(outerRadius, 0.01, 16, 18, 360 * DEG2RAD), new MeshBasicMaterial({
            color: hitCylinderColor,
            side: DoubleSide,
            opacity: HIT_OPACITY,
            transparent: true
        }));
        this.outerTorus.rotation.x = 90 * DEG2RAD;
        hitMarker.add(this.outerTorus);
        const viewArrow = new Mesh(new ConeGeometry(0.08, 0.15), new MeshBasicMaterial({
            color: hitCylinderColor,
            transparent: true
        }));
        viewArrow.rotation.x = -90 * DEG2RAD;
        viewArrow.position.z = -(0.6 + 0.15 / 2);
        viewArrow.updateMatrix();
        this.viewArrowGroup.add(viewArrow);
        hitMarker.add(this.viewArrowGroup);
        return hitMarker;
    }
    checkParabolicCurveIntersection(p0, v0, hitPoint) {
        return __awaiter(this, void 0, void 0, function* () {
            const out = tmpVector31;
            const params = {
                rays: []
            };
            const timeSegment = 1 / (this.rayCurve.numPoints - 1);
            const prevPosition = tmpVector32.copy(p0);
            for (let i = 1; i < this.rayCurve.numPoints; i++) {
                const t = i * timeSegment;
                this.parabolicCurve(p0, v0, t, out);
                const direction = tmpVector33.subVectors(out, prevPosition);
                this.raycaster.far = direction.length();
                this.raycaster.set(prevPosition, direction.normalize());
                const intersects = navMeshService.raycast(this.raycaster, navMeshService.currentLayerId);
                if (intersects.length && intersects[0].face) {
                    // transform hitpoint to world coordinates
                    hitPoint.copy(intersects[0].point.applyMatrix4(intersects[0].object.matrixWorld));
                    return i;
                }
                params.rays.push({
                    origin: convertToNumber3(prevPosition),
                    target: convertToNumber3(out),
                    backfaceCulling: false
                });
                prevPosition.copy(out);
            }
            return -1;
        });
    }
    parabolicCurve(p0, v0, t, out) {
        // y = p0 + v0 * t + 1/2at²
        // p0 Initial position
        // v0 Initial speed
        // t time
        // a acceleration with gravity 9.8m/s²
        out.x = p0.x + v0.x * t;
        out.y = p0.y + v0.y * t - 4.9 * t * t;
        out.z = p0.z + v0.z * t;
        return out;
    }
    updateViewArrowRotation() {
        const yaw = this.viewArrowYaw;
        if (yaw === undefined) {
            this.viewArrowGroup.visible = false;
            return;
        }
        this.viewArrowGroup.visible = true;
        // 270 Degree correction is ugly but working
        this.viewArrowGroup.setRotationFromAxisAngle(UNIT_Y, yaw + Math.atan2(this.direction.x, this.direction.z) + DEG2RAD * 270);
        this.viewArrowGroup.updateMatrix();
    }
}
